// CommentThreadItem — ONE comment in a CommentThread (VaultOS v2 design §3.5).
// Mounted by CommentThread's `List` via `childExtends: 'CommentThreadItem'`;
// its state is one reduced row from `commentThreadRows`
// (functions/commentThread.js):
//   { id, body, author, authorEmail, createdAt, editedAt, replyTo, deleted,
//     depth, ownedByMe, canRemove, replyCount }
//
// A SEPARATE component, not an inline `childExtends: {}` (FA808), so a
// consumer that needs a different row — a compact one in a canvas sheet, a
// wide one on a records page — overrides this key instead of forking the
// thread.
//
// IT DEPENDS ON ITS HOST for behaviour and nothing else: `el.scope.aimReply`
// and `el.scope.remove` are CommentThread's own scope methods, and `el.scope`
// is inherited by every descendant, so the row needs no props, no imports and
// no `el.parent` hop-counting. Mounting it outside a CommentThread renders a
// correct, inert row.
//
// Flat and borderless: a reply steps one wash deeper and indents, which is
// the whole nesting language (design-direction §8). No hairline, no shadow.
export const CommentThreadItem = {
  flow: 'y',
  gap: 'X',
  width: '100%',
  minWidth: '0',
  padding: 'Z Z1',
  round: 'radiusControl',
  // Fill contrast, not a rule: a reply steps one wash deeper and indents,
  // which is the entire nesting language here (design-direction §8).
  background: (el, s) => (s.depth ? 'selected' : 'overlay'),
  isReply: (el, s) => !!s.depth,
  '.isReply': { marginLeft: 'A' },
  // A surviving tombstone reads as the quiet ink — legible, unmistakable,
  // no strike-through noise.
  isRemoved: (el, s) => !!s.deleted,
  // D-2 (2026-09-24): this text can sit on a washed dark row, where `inactive`
  // reads 4.29:1 on `hover` and 3.33:1 on `selected` — both under 4.5 — so the
  // quiet ink here is `caption`. (A reply sits on the `selected` wash.)
  '.isRemoved': { color: 'caption' },

  Meta: {
    flow: 'x',
    align: 'center left',
    gap: 'Y',
    width: '100%',
    minWidth: '0',
    // One fontSize across the row so the author, the time and the tag
    // share a baseline (Rule 34 — spacing tokens are em-based).
    fontSize: 'Y2',
    Author: {
      tag: 'span',
      fontWeight: '700',
      color: 'title',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: '0',
      text: (el, s) => s.author || el.call('polyglot', 'shell.comments.unknown')
    },
    Time: {
      tag: 'span',
      if: (el, s) => !!s.createdAt,
      color: 'caption',
      whiteSpace: 'nowrap',
      flexShrink: '0',
      text: (el, s) => el.call('commentTimeLabel', s.createdAt)
    },
    Edited: {
      tag: 'span',
      if: (el, s) => !!s.editedAt && !s.deleted,
      color: 'caption',
      whiteSpace: 'nowrap',
      flexShrink: '0',
      text: '{{ shell.comments.edited | polyglot }}'
    }
  },

  Body: {
    tag: 'p',
    if: (el, s) => !s.deleted,
    margin: '0',
    fontSize: 'Z1',
    color: 'title',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    minWidth: '0',
    text: (el, s) => s.body
  },

  Removed: {
    tag: 'p',
    if: (el, s) => !!s.deleted,
    margin: '0',
    fontSize: 'Z1',
    fontStyle: 'italic',
    // D-2: on a reply this note sits on the `selected` wash (see isRemoved).
    color: 'caption',
    text: '{{ shell.comments.removed | polyglot }}'
  },

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    // Both affordances are text buttons on the row's own wash — no
    // outlines, no icon buttons for two words (design-direction §8/§9).
    hide: (el, s) => !!s.deleted,
    ReplyBtn: {
      tag: 'button',
      type: 'button',
      // One nesting level: a reply to a reply would render at the same
      // depth anyway, so the affordance is offered on roots only and the
      // thread cannot grow a second indent.
      hide: (el, s) => !!s.depth,
      padding: '0',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y2',
      color: 'caption',
      ':hover': { color: 'title' },
      text: '{{ shell.comments.reply | polyglot }}',
      onClick: (e, el, s) => {
        e.preventDefault()
        el.scope.aimReply(el, s.id, s.author || '')
      }
    },
    RemoveBtn: {
      tag: 'button',
      type: 'button',
      hide: (el, s) => !s.canRemove,
      padding: '0',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y2',
      color: 'caption',
      ':hover': { color: 'dangerInk' },
      text: '{{ shell.comments.remove | polyglot }}',
      onClick: (e, el, s) => {
        e.preventDefault()
        el.scope.remove(el, s.id)
      }
    }
  }
}
